import api from '../client';

// Interfaces
export interface Category {
  uuid: string;
  name: string;
  description: string;
}

export interface TotalDonationReceived {
  total_donation_requests: number;
  total_leave_value: number;
  total_employer_top_up: number;
  total_donation_received: number;
}

export interface Charity {
  uuid: string;
  name: string;
  abn: string;
  contact_name?: string | null;
  contact_email: string;
  contact_phone: string;
  website: string | null;
  logo?: string | null;
  logo_media_file?: {
    uuid: string;
    file_name: string;
    file_path: string;
    url: string;
  } | null;
  head_office_location: string;
  charity_government_id: string;
  dgr_tax_status?: string | null;
  description?: string | null;
  verified: number;
  rejected?: boolean;
  rejection_reason?: string | null;
  verified_bsb_no: string;
  verified_acc_no: string;
  verified_acc_name: string;
  bank_details_verified_at?: string | null;
  bank_details_verified_by_user_uuid?: string | null;
  bank_details_status?: 'verified' | 'unverified';
  category: Category;
  total_donation_received: TotalDonationReceived;
}

export interface EmployerContributions {
  approved: number;
  pending: number;
  rejected: number;
}

export interface DonationPayoutStatus {
  uuid: string;
  name: string;
}

export interface CharityBasic {
  uuid: string;
  name: string;
}

export interface EmployerBasic {
  uuid: string;
  name: string;
}

export interface Transaction {
  uuid: string;
  transaction_id: string;
  transaction_date: string;
  amount: number;
  charity: CharityBasic;
  donation_payout_status: DonationPayoutStatus;
  employer?: EmployerBasic;
  company?: string;
}

export interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface TransactionHistory {
  data: Transaction[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface PayoutTotals {
  total_payout_value: number;
  pending_payout_amount: number;
}

export interface CharityProfileData {
  charity: Charity;
  employer_contributions: EmployerContributions;
  transaction_history: TransactionHistory;
  payout_totals: PayoutTotals;
}

export interface CharityProfileResponse {
  success: boolean;
  message: string;
  data: CharityProfileData;
}

export class CharityProfileService {
  private static readonly baseUrl = '/admin/charity';

  /**
   * Get charity profile data including charity details, employer contributions, and transaction history
   * @param charityUuid UUID of the charity
   * @param page Optional page number for transaction history pagination
   * @param perPage Optional items per page for transaction history pagination
   * @param statusFilter Optional status filter for payment history
   * @param employerFilter Optional employer UUID filter for payment history
   */
  static async getCharityProfile(
    charityUuid: string,
    page: number = 1,
    perPage: number = 20,
    statusFilter?: string,
    employerFilter?: string
  ): Promise<CharityProfileResponse> {
    try {
      const params: any = {
        payment_page: page,
        payment_per_page: perPage
      };
      
      if (statusFilter) {
        params.payment_status = statusFilter;
      }
      
      if (employerFilter) {
        params.payment_employer_uuid = employerFilter;
      }
      
      const response = await api.get<CharityProfileResponse>(
        `${this.baseUrl}/${charityUuid}/profile`,
        { params }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Authentication required');
      }
      if (error.response?.status === 403) {
        throw new Error('User is not authorized to access admin charity data');
      }
      if (error.response?.status === 404) {
        throw new Error('Charity not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to retrieve charity profile data');
    }
  }

  /**
   * Format currency values
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(value);
  }

  /**
   * Format date values
   */
  static formatDate(date: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-AU');
  }

  /**
   * Get verification status text
   */
  static getVerificationStatus(verified: number): string {
    return verified === 1 ? 'Verified' : 'Not Verified';
  }

  /**
   * Get verification status color
   */
  static getVerificationStatusColor(verified: number): string {
    return verified === 1 ? '#059669' : '#dc2626';
  }

  /**
   * Get transaction status color
   */
  static getTransactionStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#059669';
      case 'pending':
        return '#f59e0b';
      case 'failed':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  }

  /**
   * Reject a charity (mark as rejected, store optional reason, notify inviter).
   * Optionally provide a reason; it will be stored and sent to the user who nominated them.
   */
  static async rejectCharity(uuid: string, rejectionReason?: string | null): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `/charity/${uuid}/reject`,
      rejectionReason != null && rejectionReason.trim() !== '' ? { rejection_reason: rejectionReason.trim() } : undefined
    );
    return response.data;
  }
}

// Export instance for backward compatibility
export const charityProfileService = CharityProfileService; 